// Boundary parsing for everything that arrives over a network: room packets, matchmaker updates, edge replies,
// PlayFab responses and seat claims. Each helper returns the typed value or throws WireFormatError; callers at a
// boundary catch that one type and answer with their own protocol's rejection.

import { WireFormatError } from './errors'

export type WireObject = Readonly<Record<string, unknown>>

export function expectObject(value: unknown, what: string): WireObject {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new WireFormatError(`${what} is not an object`)
  return value as WireObject
}

export function expectString(value: unknown, what: string): string {
  if (typeof value !== 'string') throw new WireFormatError(`${what} is not a string`)
  return value
}

export function expectMatching(value: unknown, pattern: RegExp, what: string): string {
  const text = expectString(value, what)
  if (!pattern.test(text)) throw new WireFormatError(`${what} is malformed`)
  return text
}

export function expectInteger(value: unknown, what: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) throw new WireFormatError(`${what} is not an integer`)
  return value
}

export function expectBoolean(value: unknown, what: string): boolean {
  if (typeof value !== 'boolean') throw new WireFormatError(`${what} is not a boolean`)
  return value
}

export function expectArray(value: unknown, what: string): readonly unknown[] {
  if (!Array.isArray(value)) throw new WireFormatError(`${what} is not an array`)
  return value
}

/** One of a closed set of string literals. */
export function expectOneOf<T extends string>(value: unknown, allowed: readonly T[], what: string): T {
  const text = expectString(value, what)
  if (!(allowed as readonly string[]).includes(text)) throw new WireFormatError(`${what} '${text}' is not one of ${allowed.join(', ')}`)
  return text as T
}

/** JSON text from a peer; a syntax error is a wire-format error like any other malformed input. */
export function parseJson(text: string, what: string): unknown {
  try {
    return JSON.parse(text)
  } catch (error) {
    if (error instanceof SyntaxError) throw new WireFormatError(`${what} is not JSON`)
    throw error
  }
}
